import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Repeat, 
  Calendar, 
  Trash2, 
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Calculator
} from "lucide-react";
import { RecurringSeries } from "@/hooks/use-recurring-transactions";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface RecurringSeriesCardProps {
  series: RecurringSeries;
  onViewDetails: (series: RecurringSeries) => void;
  onDelete: (seriesId: string) => void;
}

export const RecurringSeriesCard = ({
  series,
  onViewDetails,
  onDelete,
}: RecurringSeriesCardProps) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const progress = series.totalInstallments 
    ? (series.paidInstallments / series.totalInstallments) * 100 
    : 0;

  const isInstallment = !!series.totalInstallments;

  const frequencyLabel = {
    weekly: "Semanal",
    monthly: "Mensal",
    yearly: "Anual",
  }[series.frequency];

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div
              className={cn(
                "h-10 w-10 rounded-full flex items-center justify-center",
                series.type === "income" 
                  ? "bg-success/10 text-success" 
                  : "bg-destructive/10 text-destructive"
              )}
            >
              {series.type === "income" ? (
                <TrendingUp className="h-5 w-5" />
              ) : (
                <TrendingDown className="h-5 w-5" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-medium truncate">{series.description}</p>
                <Badge variant={series.isActive ? "default" : "secondary"} className="text-xs">
                  {series.isActive ? "Ativa" : "Encerrada"}
                </Badge>
                {series.budgetItemName && (
                  <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                    <Calculator className="h-3 w-3 mr-1" />
                    No orçamento
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Repeat className="h-3 w-3" />
                  {isInstallment 
                    ? `${series.paidInstallments}/${series.totalInstallments} parcelas`
                    : frequencyLabel
                  }
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Próx: {format(new Date(series.nextDueDate), "dd/MM", { locale: ptBR })}
                </span>
              </div>

              {isInstallment && (
                <div className="mt-2">
                  <Progress value={progress} className="h-1.5" />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <p className={cn(
              "font-bold text-lg",
              series.type === "income" ? "text-success" : "text-destructive"
            )}>
              {formatCurrency(series.amount)}
            </p>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onDelete(series.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onViewDetails(series)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
